'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import TravelHero from './TravelHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from TravelHero FEATURED + STATS. */
const METRICS = [
  { value: '75%', label: 'of travellers are inspired by social media when planning trips (Booking.com 2025)' },
  { value: '55%', label: 'of travellers say their hospitality experience expectations remain unmet (Vertex Group Survey 2025)' },
  { value: '62%', label: 'of travel problems go unresolved at the point of complaint — only 38% resolved within one hour (Vertex Group 2025)' },
];

export default function TravelBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('travel-and-hospitality');

  const CHALLENGES = [
    {
      title: t("industries_travel_and_hospitality.the_peak_season_avalanche"),
      desc: t("industries_travel_and_hospitality.a_long_weekend_or_major_travel_even"),
    },
    {
      title: t("industries_travel_and_hospitality.the_airline_crisis_window"),
      desc: t("industries_travel_and_hospitality.when_a_flight_is_delayed_or_cancell"),
    },
    {
      title: t("industries_healthcare.the_review_blind_spot"),
      desc: t("industries_travel_and_hospitality.a_hotel_chain_managing_20_propertie"),
    },
    {
      title: t("industries_travel_and_hospitality.the_fragmented_guest_journey"),
      desc: t("industries_travel_and_hospitality.a_guest_who_complains_on_instagram"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_travel_and_hospitality.what_is_a_travel_hospitality_custom"),
      a: t("industries_travel_and_hospitality.one_workspace_for_guest_grievance_m"),
    },
    {
      q: t("industries_travel_and_hospitality.how_does_locobuzz_help_hotels_manag"),
      a: t("industries_travel_and_hospitality.hotel_reviews_from_tripadvisor_goog"),
    },
    {
      q: t("industries_travel_and_hospitality.can_locobuzz_help_airlines_manage_t"),
      a: t("industries_travel_and_hospitality.yes_crisis_mode_activates_automatic"),
    },
    {
      q: t("industries_travel_and_hospitality.how_does_locobuzz_handle_the_guest"),
      a: t("industries_travel_and_hospitality.crisis_mode_pre_configured_before_t"),
    },
    {
      q: t("industries_travel_and_hospitality.can_locobuzz_support_otas_managing"),
      a: t("industries_travel_and_hospitality.yes_complaints_from_social_media_re"),
    },
  ];

  const BG_TITLE = t('industries_travel_and_hospitality.why_is_hotel_reputation_management');
  const BG_ACCENT = 'Travel Brands Can';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">{t("industries_travel_and_hospitality.locobuzz_is_an_ainative_customer_ex")}</span>
      <span className="sr-only">{t("industries_travel_and_hospitality.travel_and_hospitality_is_growing_f")}</span>
      <span className="sr-only">{t("industries_travel_and_hospitality.locobuzz_maps_six_purposebuilt_capa")}</span>
      <span className="sr-only">{t("industries_travel_and_hospitality.travel_and_hospitality_brands_using")}</span>

      <TravelHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_travel_and_hospitality.why_hotel_reputation_management_is")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_travel_and_hospitality.the_pressure_peaks_during_highseaso")}
        imageSrc="/industries/why/travel-and-hospitality.png"
        imageAlt={t("industries_travel_and_hospitality.travel_and_hospitality_guest_review")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_travel_and_hospitality.what_guest_complaint_reputation_cha")}
        items={CHALLENGES}
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="travel-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
